/**
 * Formik-bound QueryBuilder wrapper for MOI vendor assignment rules.
 * Sibling of `FundAccountConditionsQueryBuilderFormik` — same skeleton (read `values[fieldId]`, gate
 * on `submitCount`, compute prereqs, call the shared registry hook, render the pure QB)
 * parameterised over the `moi_vendor_assignment_rule` rule_set_type.
 *
 * Differences from the fund-account sibling:
 *  - No event. The rule set is resolved on demand rather than by an event
 *    (`moi_vendor_assignment/rule_set_configs.py`), so there is no event key to bind: none is passed,
 *    the hook sends it empty, and the BE then applies no event scope and merges in no event payload
 *    fields. The builder is told there is no event time to compare a date against.
 *  - The LOB scope may be "All LOBs", which names no LOB, so the prerequisite accepts it in place of
 *    a concrete pick and the hook is handed it as the flag.
 */

import React, { useEffect, useRef } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { getIn, useFormikContext } from 'formik';
import { isEmpty } from 'lodash';

import useOrganization from '~/components/OrganizationContext';
import RuleConditionsQueryBuilder from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder/RuleConditionsQueryBuilder';
import { useRuleSetConditionFieldRegistry } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder/useRuleSetConditionFieldRegistry';

import { TEXTS } from '../texts';

const MOI_VENDOR_ASSIGNMENT_RULE_TYPE = 'moi_vendor_assignment_rule';

interface MoiAssignmentConditionsQueryBuilderFormikProps {
  /** Formik path holding the `conditions_json` tree (default: `'conditions_json'`). */
  fieldId?: string;
  /** The concrete LOBs the rule is scoped to. Forwarded whole: the catalog is the intersection across
   *  them, so a subset would offer fields the saved rule is rejected for. Empty with `isAllLobs`
   *  unset means "not narrowed yet". */
  lobs: string[];
  /** The rule is scoped to every LOB the organization supports, future ones included. Forwarded as
   *  the flag because no list of LOBs can express that; the BE ignores `lobs` while it is set. */
  isAllLobs: boolean;
  isAllSubOrgs: boolean;
  subOrgIds: number[];
  /** Surfaces the catalog-fetch loading state so the host form can hold Save while fields are in
   *  flight. Idle (false) while prerequisites aren't met. */
  onLoadingChange?: (isLoading: boolean) => void;
}

interface PrerequisiteCheck {
  prerequisitesMet: boolean;
  prerequisiteMessage: string | null;
}

/** A LOB scope (a concrete pick OR All LOBs) + a sub-org scope (a pick OR all-sub-orgs OR sub-orgs
 *  disabled). There is no event to pick, so none is part of the gate. */
const checkPrerequisites = (
  lobs: string[],
  isAllLobs: boolean,
  isAllSubOrgs: boolean,
  subOrgIds: number[],
  subOrgsEnabled: boolean
): PrerequisiteCheck => {
  const hasLobScope = !isEmpty(lobs) || isAllLobs;
  const hasOrgScope = !subOrgsEnabled || isAllSubOrgs || !isEmpty(subOrgIds);
  if (hasLobScope && hasOrgScope) {
    return { prerequisitesMet: true, prerequisiteMessage: null };
  }
  return { prerequisitesMet: false, prerequisiteMessage: TEXTS.CONDITIONS_PREREQUISITE(subOrgsEnabled) };
};

const MoiAssignmentConditionsQueryBuilderFormik: React.FC<MoiAssignmentConditionsQueryBuilderFormikProps> = ({
  fieldId = 'conditions_json',
  lobs,
  isAllLobs,
  isAllSubOrgs,
  subOrgIds,
  onLoadingChange,
}) => {
  const { values, setFieldValue, submitCount } = useFormikContext();
  const value = (getIn(values, fieldId) as RuleGroupType | null | undefined) ?? null;

  // `useOrganization` is exported from a JSX file without types; cast at the boundary.
  const { organizationId, subOrganizationEnabled } = useOrganization() as {
    organizationId: number;
    subOrganizationEnabled?: boolean;
  };

  const { prerequisitesMet, prerequisiteMessage } = checkPrerequisites(
    lobs,
    isAllLobs,
    isAllSubOrgs,
    subOrgIds,
    !!subOrganizationEnabled
  );

  const { fields, isLoading } = useRuleSetConditionFieldRegistry({
    organizationId,
    enabled: prerequisitesMet,
    ruleSetType: MOI_VENDOR_ASSIGNMENT_RULE_TYPE,
    // No `eventKey`: the rule set has no event, so there is no event scope to narrow the catalog by.
    lobs,
    isAllLobs,
    isAllSubOrgs,
    subOrgIds,
  });

  // Fire only when the loading boolean flips, never on the callback's identity — an inline arrow
  // from a caller would otherwise re-run this every render. Holding the callback in a ref keeps
  // the latest one reachable without putting it in the dependency list, so no lint suppression is
  // needed here.
  const onLoadingChangeRef = useRef(onLoadingChange);
  useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange;
  }, [onLoadingChange]);

  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);

  return (
    <RuleConditionsQueryBuilder
      value={value}
      onChange={(q) => setFieldValue(fieldId, q)}
      shouldShowErrors={submitCount > 0}
      fields={fields}
      isLoading={isLoading}
      prerequisiteMessage={prerequisiteMessage}
      emptyMessage={TEXTS.CONDITIONS_EMPTY}
      // The rule set has no triggering event, so there is no event time for a date leaf to compare against.
      hasTriggeringEvent={false}
    />
  );
};

export default MoiAssignmentConditionsQueryBuilderFormik;
